/**
 * Pitch — the VC-gated deck and its chrome (side-dot navigator,
 * idle Continue/Back-to-start pill, keyboard nav, scroll-spy scene
 * tracking).
 *
 * Phase 4 of the Oatmeal rebuild (`docs/eng/pilot-oatmeal-rebuild.md`)
 * folded the prior `PitchSections.tsx` JSX in-line as part of this
 * file — single-view-single-file per the rebuild's invariant.
 *
 * Section composition note (carries from Phases 2-3): Pitch's slides
 * are marketing-shaped enough that some vendored Oatmeal *page
 * sections* would plausibly fit (`hero-centered`, `stats-three-column`,
 * `team-three-column-grid`, `call-to-action-simple`). Phase 4
 * intentionally **doesn't** swap them in — the existing scenes use
 * the local `<Scene>` helper for scroll-snap deck semantics and the
 * Oatmeal *element* primitives (Heading/Subheading/Text/Eyebrow/Button)
 * which is what "compose over vendor" actually requires. Future
 * follow-up may revisit specific scenes for canonical section
 * adoption, but the rebuild's primary deliverable (fold + element
 * composition + cleanup) is in place.
 *
 * Mount model
 * -----------
 * Pitch is mounted by `Pilot.tsx` only while `viewMode === 'pitch'`.
 * Before Phase 4, the deck `<section id="pitch">` was rendered
 * unconditionally by `PilotBody` and hidden with CSS; the chrome
 * (this file) mounted conditionally on top. After the fold the deck
 * is conditional too — Pitch.tsx owns both. When the visitor leaves
 * pitch mode, every scroll listener, keyboard handler, and idle timer
 * tears down via React's effect cleanup.
 *
 * Architectural invariant
 * -----------------------
 *   > The React tree is the only writer of pilot DOM state.
 *
 * This component does NOT touch `document.body.classList`. The
 * `pitch-mode` body class lives in `PilotProvider`'s effect (see
 * PilotContext.tsx).
 *
 * DOM contract preserved for `main.js`
 * ------------------------------------
 *   #pitch-talk-cta — the "Book a conversation →" CTA on the closing
 *   scene. `main.js` binds an `onclick` to open the talk modal at
 *   startup. The id is rendered in the deck JSX below.
 */

import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {clsx} from 'clsx/lite';
import {useScrollSpy, type ScrollSpyTarget} from '../../hooks/useScrollSpy';
import {Heading} from '../../oatmeal/elements/heading';
import {Subheading} from '../../oatmeal/elements/subheading';
import {Text} from '../../oatmeal/elements/text';
import {Eyebrow} from '../../oatmeal/elements/eyebrow';
import {Button} from '../../oatmeal/elements/button';
import {Scene} from './_helpers';
import {STORY_DOT_CLASS, STORY_DOT_ACTIVE_CLASS} from './SceneDots';
import {PILOT_OPEN_TALK_MODAL_EVENT} from '../../lib/pilot/events';

const IDLE_PILL_MS = 4000;

/**
 * White-card styling for each pitch slide's inner `<Container>`. The
 * Pitch deck reuses Story's `<Scene>` helper (which renders into the
 * page-level taupe-100 / taupe-950 ground), and Instrument Serif
 * headlines wash out against that ground per visual QA. Wrapping each
 * slide's Container in a bright white card with hairline border +
 * soft shadow gives the prose a brighter ground.
 *
 * Descendant overrides (`[&_h1]:text-taupe-950`, etc.) force the
 * heading + text colors to stay readable inside the white card even
 * when the visitor's theme is dark (the Oatmeal default flips text to
 * taupe-50 in dark mode, which is unreadable on a white card).
 *
 * F17 Slice C: the prior `body.pitch-mode .pitch > .story-scene >
 * .mx-auto` CSS rules in pilot-engine.css are gone — this single
 * inline Tailwind string replaces all of them, and Pitch.tsx is
 * conditionally mounted by Pilot.tsx (only when viewMode === 'pitch')
 * so no body-class gate is needed.
 */
const PITCH_CONTAINER_CLASS =
  'bg-white border border-taupe-200 rounded-3xl ' +
  '[box-shadow:0_1px_2px_rgba(0,0,0,0.03),0_16px_40px_rgba(0,0,0,0.04)] ' +
  'px-[clamp(1.5rem,4vw,4rem)] py-[clamp(2.5rem,4vw,4rem)] ' +
  'my-[clamp(2rem,6vh,4rem)] ' +
  '[&_h1]:text-taupe-950 [&_h2]:text-taupe-950 ' +
  '[&_.story-display]:text-taupe-950 [&_.story-h2]:text-taupe-950 ' +
  '[&_.story-lede]:text-taupe-800 [&_.pitch-body]:text-taupe-800 ' +
  '[&_.story-scroll-hint]:hidden';

/**
 * Local PitchScene wrapper — passes the card styling through Scene's
 * `containerClassName` prop and adds the hairline divider for
 * adjacent slides via Tailwind's adjacent-sibling utility. Every
 * pitch slide goes through this so the wrapping stays single-sourced.
 */
function PitchScene({
  eyebrow,
  className,
  children,
}: {
  eyebrow?: ReactNode;
  className?: string;
  children: ReactNode;
}) {
  return (
    <Scene
      eyebrow={eyebrow}
      className={clsx(
        'story-scene [&+.story-scene]:border-t [&+.story-scene]:border-taupe-200',
        className,
      )}
      containerClassName={PITCH_CONTAINER_CLASS}
    >
      {children}
    </Scene>
  );
}

const PREFERS_REDUCED_MOTION =
  typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
    : false;

// Scene labels mirror the eyebrow on each `.story-scene` inside
// `PitchSections.tsx`. The labels drive the dot stack `aria-label`s
// and the bottom-right scene-summary text. Order MUST match the
// order of scenes in PitchSections — adding/removing a scene there
// requires an update here. Both are owned by Phase 6.
const SCENE_LABELS: readonly string[] = [
  'Planapus · 2026',
  "Why we're building this",
  'Why now',
  'The opportunity',
  'Our wedge',
  'What Planapus does',
  'Early signals',
  'How we become hard to replicate',
  'Team',
  'Plan & ask',
  'If this is your problem too',
];

export default function Pitch() {
  // Scene element list, captured from the global DOM after Pitch
  // mounts. The deck JSX below renders `#pitch` with its `.story-scene`
  // children, and the useEffect runs after first paint — so the
  // querySelector reliably finds them on initial mount. We don't track
  // scenes in React state once captured because they're stable across
  // this component's lifetime — they exist before the effect runs and
  // don't change until unmount.
  const [sceneEls, setSceneEls] = useState<HTMLElement[]>([]);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const found = Array.from(
      document.querySelectorAll<HTMLElement>('#pitch .story-scene'),
    );
    setSceneEls(found);
  }, []);

  // Adapt the discovered scene elements into Phase 4's useScrollSpy
  // signature (target list with refs + string ids). Synthetic refs
  // (`{current: el}`) are accepted because the hook only reads
  // `ref.current` to scroll/observe; React doesn't need to own them.
  const spyTargets = useMemo<ScrollSpyTarget[]>(
    () =>
      sceneEls.map((el, i) => ({
        id: `pitch-scene-${i}`,
        ref: {current: el} as React.RefObject<HTMLElement>,
      })),
    [sceneEls],
  );

  const {activeId, scrollTo} = useScrollSpy(spyTargets, {
    disabled: sceneEls.length === 0,
  });

  // Convert the hook's string id back to an index for the rest of the
  // component's logic (idle pill, keyboard nav, pill action).
  const activeIdx = useMemo(() => {
    if (!activeId) return 0;
    const m = /^pitch-scene-(\d+)$/.exec(activeId);
    return m ? Number(m[1]) : 0;
  }, [activeId]);

  const scrollToIdx = useCallback(
    (idx: number) => {
      if (idx < 0 || idx >= sceneEls.length) return;
      scrollTo(`pitch-scene-${idx}`);
    },
    [scrollTo, sceneEls.length],
  );

  const isLastScene = activeIdx === sceneEls.length - 1 && sceneEls.length > 0;

  // ---- Idle pill --------------------------------------------------
  //
  // The pill fades after IDLE_PILL_MS of no scene change. Reduced-
  // motion visitors keep the pill at full opacity.
  const [pillIdle, setPillIdle] = useState(false);

  useEffect(() => {
    if (PREFERS_REDUCED_MOTION) return;
    setPillIdle(false);
    const t = window.setTimeout(() => setPillIdle(true), IDLE_PILL_MS);
    return () => window.clearTimeout(t);
  }, [activeIdx]);

  // ---- Pill action ------------------------------------------------
  const advance = useCallback(() => {
    if (sceneEls.length === 0) return;
    if (activeIdx >= sceneEls.length - 1) {
      scrollToIdx(0);
    } else {
      scrollToIdx(activeIdx + 1);
    }
  }, [activeIdx, sceneEls.length, scrollToIdx]);

  const back = useCallback(() => {
    scrollToIdx(Math.max(0, activeIdx - 1));
  }, [activeIdx, scrollToIdx]);

  // ---- Keyboard navigation ---------------------------------------
  useEffect(() => {
    if (sceneEls.length === 0) return;
    // Arrow keys + PageUp/PageDown navigate the deck unconditionally
    // (except when focus is in a text-entry control). Space/Enter
    // navigate only when focus is somewhere "passive" — if the
    // visitor tabbed to an interactive button/anchor (e.g.
    // #pitch-talk-cta on the closing scene), Space/Enter must
    // activate that control, not the deck.
    const arrowFwd = new Set(['ArrowDown', 'ArrowRight', 'PageDown']);
    const arrowBwd = new Set(['ArrowUp', 'ArrowLeft', 'PageUp']);
    const fwdActivate = new Set([' ', 'Enter']);
    const bwdActivate = new Set(['Backspace']);

    function isTextEntry(t: EventTarget | null): boolean {
      const el = t as HTMLElement | null;
      if (!el || !el.tagName) return false;
      if (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA') return true;
      if (el.isContentEditable) return true;
      return false;
    }

    function isInteractive(t: EventTarget | null): boolean {
      const el = t as HTMLElement | null;
      if (!el || typeof el.closest !== 'function') return false;
      return el.closest('button, a, [role="button"]') !== null;
    }

    function onKey(e: KeyboardEvent) {
      // Text fields always win — typing should never advance the deck.
      if (isTextEntry(e.target)) return;

      if (arrowFwd.has(e.key)) {
        e.preventDefault();
        advance();
        return;
      }
      if (arrowBwd.has(e.key)) {
        e.preventDefault();
        back();
        return;
      }
      // For Space / Enter / Backspace, defer to the focused control
      // so the closing-scene CTA (#pitch-talk-cta) and other tabbable
      // buttons activate as expected — but advance the deck when the
      // visitor's focus is "loose" (body, scene wrapper).
      if (isInteractive(e.target)) return;
      if (fwdActivate.has(e.key)) {
        e.preventDefault();
        advance();
      } else if (bwdActivate.has(e.key)) {
        e.preventDefault();
        back();
      }
    }

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [advance, back, sceneEls.length]);

  // ---- On-mount: jump to top of deck -----------------------------
  //
  // When the visitor switches into Pitch mode, the previous scroll
  // position belongs to whichever view they came from. Reset to the
  // top so the deck begins at scene 1.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    document.documentElement.scrollTop = 0;
  }, []);

  // Labels list — pad/truncate to the actual scene count in case the
  // deck changed shape without updating SCENE_LABELS.
  const labels = useMemo(() => {
    const out: string[] = [];
    for (let i = 0; i < sceneEls.length; i++) {
      out.push(SCENE_LABELS[i] ?? `Slide ${i + 1}`);
    }
    return out;
  }, [sceneEls.length]);

  // Note on class names: we intentionally use `pitch-controls` /
  // `pitch-dots` / `pitch-pill` instead of the `story-controls` /
  // `story-dots` / `story-pill` class names that lib/pilot/pitch.js
  // shared with Story. The engine CSS rule
  // `body:not(.story-mode) .story-controls{display:none}` would have
  // hidden Pitch's chrome by symmetry — Phase 6 dodges that conflict
  // by giving Pitch its own class names. Visual styling lives in the
  // Tailwind utility classes below, mirroring Story's chrome look.
  return (
    <>
      {/* Deck — eleven scrolling scenes. Folded in from the prior
          PitchSections.tsx in Phase 4. Each `.story-scene` matches one
          entry in SCENE_LABELS above; the useEffect+querySelector
          discovery below relies on document order. */}
      <section className="pitch" id="pitch">
        <PitchScene className=" pitch-scene-title min-h-screen">
          <Eyebrow className="story-eyebrow">Planapus · 2026</Eyebrow>
          <Heading className="story-display">
            Planapus turns <em>process documents</em> into working apps.
          </Heading>
          <Text size="lg" className="story-lede">
            Every process document (a permit packet, a compliance binder, an
            underwriting checklist) is a blueprint for an app that hasn't
            been built. We build the app.
          </Text>
          {/* No inline "Scroll ↓" hint — the persistent #pitch-pill rendered
              below is the canonical advance affordance for the deck. Two
              indicators on the same scene was audit #6's duplicate-CTA
              finding for the pitch deck. */}
        </PitchScene>

        <PitchScene eyebrow="Why we're building this" className="">
          <Subheading className="story-h2">
            The AI-native coordination layer for the enterprise.
          </Subheading>
          <Text size="lg" className="story-lede">
            The founders built and led the agreement platform and
            extensibility at Docusign, and watched the same pattern in every
            large customer: documents got signed, but the work around them
            stayed broken.
          </Text>
          <Text className="pitch-body">
            Vendor onboarding, employee service requests and exceptions,
            financial-services account opening, government applications:
            hampered by errors, delays, and downstream costs.{' '}
            <strong className="text-taupe-950 dark:text-white">
              Planapus turns that work into smooth operations, coordinated by
              AI.
            </strong>
          </Text>
        </PitchScene>

        <PitchScene eyebrow="Why now" className="">
          <Subheading className="story-h2">
            What took us a decade at Docusign, AI now compresses to{' '}
            <em>months</em>.
          </Subheading>
          <Text className="pitch-body">
            Two curves cross at the same time.{' '}
            <strong className="text-taupe-950 dark:text-white">
              Enterprises are budgeting for AI that takes work off the
              operation
            </strong>
            , not chatbots, not pilots. The mandate is real and the line items
            are funded for the first time.
          </Text>
          <Text className="pitch-body">
            And the model capability finally meets the work: frontier models
            can extract requirements from messy documents, reason over
            sequencing and dependencies, and run the coordination loop.{' '}
            <strong className="text-taupe-950 dark:text-white">
              The integrations, partner ecosystem, and app platform we shipped
              at Docusign over 10 years: agentic AI does that scope of work
              in weeks.
            </strong>{' '}
            The same team, the same playbook, at a scale that wasn't
            physically possible before.
          </Text>
        </PitchScene>

        <PitchScene eyebrow="The opportunity" className="">
          <Subheading className="story-h2">
            Much of the economy still runs on{' '}
            <em>process logic trapped in documents</em>.
          </Subheading>
          <Text className="pitch-body">
            Process rules within static documents, fragmented across official
            and open sources, with no software on the applicant side. A
            structural problem recurring across the economy.
          </Text>
          <Text className="pitch-body">
            Commercial insurance, wealth management, construction permitting,
            healthcare credentialing, environmental compliance, professional
            licensing. Combined process burden in the{' '}
            <strong className="text-taupe-950 dark:text-white">
              hundreds of billions of dollars annually
            </strong>
            . The industry digitized the highest-value processes. The rest is
            still manual.
          </Text>
        </PitchScene>

        <PitchScene eyebrow="Our wedge" className="">
          <Subheading className="story-h2">
            The Docusign customers who want <em>more than a signed PDF</em>.
          </Subheading>
          <Text className="pitch-body">
            We spent years inside Docusign. The complaint we kept hearing,
            and still hear, is the same: they sign the agreement, but the
            actual work around it (intake, reviews, chase, fulfillment,
            permits, exceptions) falls back into email and spreadsheets.{' '}
            <strong className="text-taupe-950 dark:text-white">
              Many are actively looking to leave for something that runs the
              operation, not just the signature.
            </strong>{' '}
            We know who they are. We built the platform they bought.
          </Text>
          <div className="pitch-roles grid grid-cols-1 gap-6 md:grid-cols-3">
            <PitchRole
              eyebrow="Wealth firms & custodians"
              body={
                <>
                  Sign the agreement on Docusign, then spend{' '}
                  <strong className="text-taupe-950 dark:text-white">
                    $30K per submitted package of PDFs
                  </strong>{' '}
                  on OCR + manual review to actually open the account.
                  Doc-gen and eSign solved one step. The rest is human.
                </>
              }
            />
            <PitchRole
              eyebrow="Insurance brokers & carriers"
              body={
                <>
                  Independent agents write most commercial-lines premium but
                  still manually fill PDF ACORD forms and email them to
                  carriers, after the engagement is signed. Even modern
                  life-insurance platforms still email PDFs.
                </>
              }
            />
            <PitchRole
              eyebrow={
                <>
                  Manufacturing{' '}
                  <span className="pitch-tag ml-2 inline-flex items-center rounded-full bg-taupe-200 px-2 py-0.5 text-[10px] font-medium uppercase tracking-widest text-taupe-700 dark:bg-taupe-800 dark:text-taupe-300">
                    design partner
                  </span>
                </>
              }
              body={
                <>
                  <strong className="text-taupe-950 dark:text-white">
                    FGF Brands
                  </strong>{' '}
                  ($1B+ revenue, makes donuts for Dunkin') runs dozens of
                  government processes per facility, all PDF-based, all
                  manual, all coordinated by a GC and a paralegal in a
                  spreadsheet. Docusign signs the docs. Nothing runs the work.
                </>
              }
            />
          </div>
          <Text className="pitch-body pitch-callout border-l-2 border-taupe-300 pl-4 dark:border-taupe-700">
            The pattern is identical:{' '}
            <em>
              documents drive the work, but the work itself is
              human-coordinated
            </em>
            . We're building the platform these customers wished Docusign had
            become.
          </Text>
        </PitchScene>

        <PitchScene eyebrow="What Planapus does" className="">
          <Subheading className="story-h2">
            Reads your documents and the work around them. Surfaces the
            operations your company is actually running.
          </Subheading>
          <div className="pitch-roles grid grid-cols-1 gap-6 md:grid-cols-3">
            <PitchRole
              eyebrow="Process owners"
              body="Compliance, ops, and function leaders point Planapus at source documents and the activity around them. The system extracts requirements, sequencing, dependencies, and participant roles, then surfaces an operation that's hosted, shareable, and auto-updating when sources change."
            />
            <PitchRole
              eyebrow="Participants"
              body="Entity data, officer details, customer info, and prior submissions carry over between operations. Step-by-step guidance with conversational support. Each subsequent run gets faster as the company's operations graph fills in."
            />
            <PitchRole
              eyebrow="Executives"
              body="One live view of every human-coordinated operation in the org, what each one costs, where it leaks time and money, and the dollar value of switching it to AI coordination."
            />
          </div>
        </PitchScene>

        <PitchScene eyebrow="Early signals" className="">
          <Subheading className="story-h2">
            Buyers describing the problem in their own words.
          </Subheading>
          <PitchQuote
            quote='"It’s me and my paralegal tracking everything in a spreadsheet. In an acquisition, we pay outside counsel just to research what permits exist and what needs to transfer, and that’s before anyone’s filed anything. We still get calls from agencies saying we missed a field. A single error can set you back weeks, and when you’re trying to close in thirty days, that’s not a small problem."'
            attribution="Venessa Vuia · General Counsel, FGF Brands, $1B+ revenue, 22 facilities"
          />
          <PitchQuote
            quote='"The USDA won’t say yes until they get the water report, and the water report lady won’t give you the report until you have the certificate of occupancy."'
            attribution="Herbert Prokscha · 25-year food-industry CEO"
          />
        </PitchScene>

        <PitchScene eyebrow="How we become hard to replicate" className="">
          <Subheading className="story-h2">
            Function-by-function expertise <em>inside the company</em>. Land,
            then expand.
          </Subheading>
          <div className="pitch-roles grid grid-cols-1 gap-6 md:grid-cols-3">
            <PitchRole
              eyebrow="1 · Read the work, not just the document"
              body="Documents are public. The activity around them (who routes, who reviews, where it stalls, what counts as 'done') is not. We read both, and surface the operations a company is actually running. That’s the moat: the latent operating system most enterprises have never written down."
            />
            <PitchRole
              eyebrow="2 · Compounding customer data"
              body="Entity data, officer details, prior submissions, exception patterns, approval history. Every operation a company runs through us adds to a structured profile that pre-populates the next. The 100th run is faster and cheaper than the first. Switching cost lives in the data, not the feature set."
            />
            <PitchRole
              eyebrow="3 · The company operations graph"
              body="We land in one function: vendor onboarding, account opening, permits, employee service. The same coordination primitives extend across the next function over. Each function adds nodes to the company’s operations graph, and the executive view gets richer with every expansion."
            />
          </div>
        </PitchScene>

        <PitchScene eyebrow="Team" className="">
          <Subheading className="story-h2">
            Built <em>Docusign's extensibility platform</em>. Now building the
            platform for processes.
          </Subheading>
          <div className="pitch-team grid grid-cols-1 gap-8 md:grid-cols-3">
            <PitchPerson
              avatar="/cast/ahmed.png"
              name="Ahmed Kamel"
              body="Engineering leader, 18+ years at Microsoft and Docusign. Pitched and built Docusign's Extensibility Platform (app marketplace, developer ecosystem, partner layer) central to its transition into a platform company."
            />
            <PitchPerson
              avatar="/cast/david.png"
              name="David Gonzalez"
              body="Docusign + Microsoft, 18+ years. Built 22+ integrations, organized 12 cross-org teams, led 10 engineers on Docusign's App and Extensibility Platform. Ships nights and weekends from his personal GitHub via a Codex + Claude agentic-loop workflow."
            />
            <PitchPerson
              avatar="/cast/parsa.png"
              name="Parsa Pezeshki"
              body="Product leader, startup guy, lawyer. Founding experience in legal tech (×3). Clause founding team through the Docusign acquisition. Took a new product 0→1 to 30K+ customers and multi-million-dollar deals in ~18 months."
            />
          </div>
          <Text className="pitch-body">
            Together: built Docusign's extensibility platform, including
            integrations, partners, app marketplace, and developer experience.
            Millions of monthly transactions in 1.5 years. 80K+ business
            accounts adopted.
          </Text>
        </PitchScene>

        <PitchScene eyebrow="Plan & ask" className="">
          <Subheading className="story-h2">
            Raising a <em>pre-seed</em> to land 3–5 design partners in 12
            months.
          </Subheading>
          <Text className="pitch-body">
            <strong className="text-taupe-950 dark:text-white">
              Pre-seed structured to bridge a Seed at month 12
            </strong>{' '}
            · use of funds: ~60% engineering, ~30% go-to-market, ~10% buffer.
            Bridges us to a{' '}
            <strong className="text-taupe-950 dark:text-white">
              Seed round at month 12
            </strong>
            , raised on the back of paid design-partner contracts.
          </Text>
          <div className="pitch-timeline grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[
              ['M1–3', 'Land design partners', '20+ buyer conversations · 2–3 paid pilots with ex-Docusign accounts'],
              ['M4–6', 'Convert pilots', 'Pilots → annual contracts · expand to a second function within each customer'],
              ['M7–9', 'Compound', '3–5 paying customers · operations graph thickens · cross-function expansion'],
              ['M10–12', 'Seed round', 'Compounding ARR from design-partner contracts · raise Seed to scale GTM'],
            ].map(([when, head, body]) => (
              <div
                key={when}
                className="pitch-tl-col flex flex-col gap-2 rounded-2xl border border-taupe-200 bg-white p-4 dark:border-taupe-800 dark:bg-taupe-900"
              >
                <div className="pitch-tl-when font-mono text-xs/5 uppercase tracking-widest text-taupe-500">
                  {when}
                </div>
                <div className="pitch-tl-in font-display text-lg/7 text-taupe-950 dark:text-white">
                  {head}
                </div>
                <div className="pitch-tl-out text-xs/5 text-taupe-700 dark:text-taupe-400">
                  {body}
                </div>
              </div>
            ))}
          </div>
          <Text className="pitch-body pitch-callout border-l-2 border-taupe-300 pl-4 dark:border-taupe-700">
            Looking for: design-partner enterprises that already feel the
            coordination tax, plus pre-seed capital from investors who back
            founder-led platform plays.
          </Text>
        </PitchScene>

        <PitchScene className=" pitch-scene-close">
          <Eyebrow className="story-eyebrow">If this is your problem too</Eyebrow>
          <Heading className="story-display pitch-close-h1">
            <em>Let's talk.</em>
          </Heading>
          <Text size="lg" className="story-lede">
            Whether you're an operator drowning in coordination tax, an
            enterprise leader sketching out an AI-coordination roadmap, or an
            investor looking at the layer underneath every back-office
            process, we want to hear from you.
          </Text>
          <Text className="pitch-body">
            Twenty minutes. No deck. Bring one process that bleeds.
          </Text>
          <div>
            <Button
              className="btn-primary pitch-cta pitch-close-cta"
              id="pitch-talk-cta"
              size="lg"
              onClick={() => {
                window.dispatchEvent(
                  new CustomEvent(PILOT_OPEN_TALK_MODAL_EVENT),
                );
              }}
            >
              Book a conversation →
            </Button>
          </div>
        </PitchScene>
      </section>

      {/* Side dot stack — keyboard-reachable; active dot carries
          aria-current="step" so screen readers announce the visitor's
          position in the deck. The .story-dot class is reused for the
          dot circle visual (defined in pilot-engine.css). */}
      <nav
        className="pitch-dots fixed left-6 top-1/2 z-30 flex -translate-y-1/2 flex-col gap-3"
        id="pitch-dots"
        aria-label="Pitch slides"
      >
        {labels.map((label, idx) => (
          <button
            key={idx}
            type="button"
            className={clsx(STORY_DOT_CLASS, idx === activeIdx && STORY_DOT_ACTIVE_CLASS)}
            aria-label={label}
            aria-current={idx === activeIdx ? 'step' : undefined}
            onClick={() => scrollToIdx(idx)}
          />
        ))}
      </nav>

      {/* Bottom-right controls — scene summary, advance/restart pill,
          keyboard hint. */}
      <div
        className="pitch-controls fixed bottom-6 right-6 z-30 flex items-end gap-4"
        id="pitch-controls"
      >
        <div
          className="pitch-scene-summary text-xs/5 text-taupe-600 dark:text-taupe-500"
          id="pitch-scene-summary"
        >
          {labels[activeIdx] ?? ''}
        </div>
        <button
          type="button"
          id="pitch-pill"
          aria-label={isLastScene ? 'Back to start' : 'Continue to next slide'}
          className={clsx(
            'pitch-pill inline-flex items-center gap-2 rounded-full bg-taupe-950 px-4 py-2 text-sm/6 font-medium text-white shadow-lg transition-opacity duration-300 hover:bg-taupe-800 dark:bg-taupe-100 dark:text-taupe-950 dark:hover:bg-white',
            pillIdle && 'opacity-30',
          )}
          onClick={isLastScene ? () => scrollToIdx(0) : advance}
        >
          {isLastScene ? '↑ Back to start' : 'Continue ↓'}
        </button>
        <div
          id="pitch-kbd-hint"
          className="pitch-kbd-hint hidden font-mono text-[10px] uppercase tracking-widest text-taupe-700 sm:inline dark:text-taupe-300"
          aria-label="Use arrow keys to navigate slides"
          title="Arrow keys: navigate slides"
        >
          ↑↓
        </div>
      </div>
    </>
  );
}

/* ============================================================
   Inline pitch helpers (folded from PitchSections in Phase 4)
   ============================================================ */
function PitchRole({eyebrow, body}: {eyebrow: ReactNode; body: ReactNode}) {
  return (
    <div className="pitch-role flex flex-col gap-2 rounded-2xl border border-taupe-200 bg-white p-5 dark:border-taupe-800 dark:bg-taupe-900">
      <div className="pitch-role-eyebrow font-mono text-xs/5 uppercase tracking-widest text-taupe-700 dark:text-taupe-400">
        {eyebrow}
      </div>
      <p className="text-sm/7 text-taupe-700 dark:text-taupe-400">{body}</p>
    </div>
  );
}

function PitchQuote({quote, attribution}: {quote: string; attribution: string}) {
  return (
    <blockquote className="pitch-quote flex flex-col gap-3 border-l-2 border-taupe-300 pl-6 dark:border-taupe-700">
      <p className="font-display text-2xl/9 text-taupe-950 dark:text-white">{quote}</p>
      <footer className="text-sm/6 text-taupe-600 dark:text-taupe-500">{attribution}</footer>
    </blockquote>
  );
}

function PitchPerson({avatar, name, body}: {avatar: string; name: string; body: ReactNode}) {
  return (
    <div className="pitch-person flex flex-col gap-3">
      <img
        className="pitch-person-avatar size-20 rounded-full object-cover"
        src={avatar}
        alt={name}
        loading="lazy"
      />
      <div className="pitch-person-name font-display text-xl/8 text-taupe-950 dark:text-white">
        {name}
      </div>
      <p className="text-sm/6 text-taupe-700 dark:text-taupe-400">{body}</p>
    </div>
  );
}
